<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { Time } from '@internationalized/date';
import { TimeFieldInput, TimeFieldRoot } from 'reka-ui';
import { cn } from '@/utils/utils';
import { isSubmitShortcut } from '@/utils/submitShortcut';
import { clockTimeFromTwelveHour, normalizeClockTime, parseClockTime } from '@/utils/customPlaylistSchedule';

/**
 * A time of day typed into one field: hour, minute and AM/PM, each changed by
 * typing or with the up and down keys. Any minute can be set. The model is the
 * column's own "HH:MM:SS" (24-hour, no time zone, seconds always 00), or null.
 * It is built on reka-ui's TimeField, as DatePicker is built on its DateField.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<{
        id?: string;
        label: string;
        invalid?: boolean;
        disabled?: boolean;
    }>(),
    { id: undefined, invalid: false, disabled: false },
);

const model = defineModel<string | null>({ default: null });

// Always a Time or null, never undefined: reka only follows the model while the
// prop is defined.
const savedTime = computed<Time | null>(() => {
    const parsed = parseClockTime(normalizeClockTime(model.value));
    return parsed ? new Time(parsed.hours, parsed.minutes) : null;
});

// The field writes a new time on every keystroke once every part holds a value,
// so retyping the minute 15 as 04 passes through 00 first. It also starts AM/PM
// as AM in an empty field, and its own value can disagree with what it shows
// (see readShownTime). So nothing the field writes reaches the model. The time
// is read off the field instead, at the moments a person would call it done:
// - fieldDirty is true while the field may show something other than the saved
//   time.
// - periodTouched is true once AM/PM was typed or stepped. Until then, in a field
//   with no saved time, AM/PM shows as empty and the time is not complete.
// - fieldKey remounts the field, which redraws it from the saved time.
const fieldDirty = ref(false);
const periodTouched = ref(false);
const fieldKey = ref(0);

const periodChosen = computed(() => savedTime.value !== null || periodTouched.value);

function segment(field: HTMLElement, part: 'hour' | 'minute' | 'dayPeriod') {
    return field.querySelector<HTMLElement>(`[data-reka-time-field-segment="${part}"]`);
}

// The part of the time an element is: 'hour', 'minute', 'dayPeriod' or a literal.
function segmentPart(element: EventTarget | null) {
    return (element as HTMLElement | null)?.getAttribute('data-reka-time-field-segment') ?? null;
}

// The time the field shows, as "HH:MM:00", or null while it is not a whole time.
// reka keeps the hour as 0–23 inside, and Backspace on 5 PM (17) leaves 1 there
// while the field reads 1 PM; AM chosen on an emptied hour gives -12. Reading
// what is shown saves what the person sees, and never an hour that is not one.
function readShownTime(field: HTMLElement): string | null {
    if (!periodChosen.value) return null;
    const hourText = segment(field, 'hour')?.textContent?.trim() ?? '';
    const minuteText = segment(field, 'minute')?.textContent?.trim() ?? '';
    const period = segment(field, 'dayPeriod')?.getAttribute('aria-valuetext');
    if (!/^(1[0-2]|[1-9])$/.test(hourText) || !/^[0-5][0-9]$/.test(minuteText)) return null;
    if (period !== 'AM' && period !== 'PM') return null;
    return clockTimeFromTwelveHour({ hour: Number(hourText), minute: Number(minuteText), period });
}

function writeModel(next: string) {
    fieldDirty.value = false;
    if (next !== normalizeClockTime(model.value)) {
        model.value = next;
    }
}

function redraw() {
    fieldDirty.value = false;
    periodTouched.value = false;
    fieldKey.value++;
}

function isDigit(event: KeyboardEvent) {
    return /^[0-9]$/.test(event.key);
}

function isStep(event: KeyboardEvent) {
    return event.key === 'ArrowUp' || event.key === 'ArrowDown';
}

function isPeriodKey(event: KeyboardEvent, part: string | null) {
    return part === 'dayPeriod' && (/^[aApP]$/.test(event.key) || isStep(event));
}

// The keys that change what the field shows.
function isEditKey(event: KeyboardEvent, part: string | null) {
    return isDigit(event) || isStep(event) || event.key === 'Backspace' || isPeriodKey(event, part);
}

// The keys that save a complete time once the field has redrawn. Up and down
// step a part to a real time. A digit that finishes a part moves the cursor on;
// until then the part may still be waiting for its second digit, as the "1" of a
// "15" is. Choosing AM or PM finishes the time.
function isCommitKey(event: KeyboardEvent, part: string | null, focusMoved: boolean) {
    return isStep(event) || (isDigit(event) && focusMoved) || isPeriodKey(event, part);
}

// This runs after the field's own key handling, because the event bubbles up
// from the part to the field.
// - A submit shortcut saves at once: the dialog's own handler submits in this
//   same event, after this one. It is the only key that saves synchronously.
// - Every other commit key saves once the field has redrawn what it typed.
function onFieldKeydown(event: KeyboardEvent) {
    if (props.disabled) return;
    const field = event.currentTarget as HTMLElement;
    const part = segmentPart(event.target);
    if (isPeriodKey(event, part)) {
        periodTouched.value = true;
    }
    if (isEditKey(event, part)) {
        fieldDirty.value = true;
    }
    if (isSubmitShortcut(event)) {
        const shown = readShownTime(field);
        if (shown) writeModel(shown);
        return;
    }
    const focusMoved = document.activeElement !== event.target;
    if (!isCommitKey(event, part, focusMoved)) return;
    nextTick(() => {
        const shown = readShownTime(field);
        if (shown) writeModel(shown);
    });
}

// Moving between parts fires this too, so it only acts once focus has settled
// outside the field. Leaving saves a complete time. Leaving an incomplete one
// redraws the field from the saved time, so the field never shows a time the
// form does not hold.
function onFieldFocusout(event: FocusEvent) {
    const field = event.currentTarget as HTMLElement;
    nextTick(() => {
        if (field.contains(document.activeElement) || !fieldDirty.value) return;
        const shown = readShownTime(field);
        if (!shown) {
            redraw();
            return;
        }
        writeModel(shown);
        // A parent that does not take the new time, as a form that refuses a
        // change would, leaves the field showing a time the model does not hold.
        nextTick(() => {
            if (readShownTime(field) !== normalizeClockTime(model.value)) redraw();
        });
    });
}

// A time set from outside, such as a form reset, replaces whatever is half typed.
// The field redraws itself for a new time, but it keeps half-typed parts when the
// time goes to null, so that case remounts it.
watch(model, (next) => {
    fieldDirty.value = false;
    if (!normalizeClockTime(next)) {
        periodTouched.value = false;
        fieldKey.value++;
    }
});
</script>

<template>
    <div
        v-bind="$attrs"
        :class="cn(
            'border-input dark:bg-input/30 flex h-9 w-fit min-w-0 items-center rounded-md border bg-transparent px-3 text-sm shadow-xs transition-[color,box-shadow]',
            'focus-within:border-ring focus-within:ring-ring/50 focus-within:ring-[3px]',
            props.invalid && 'border-destructive',
            props.disabled && 'cursor-not-allowed opacity-50',
        )"
        :data-invalid="props.invalid ? '' : undefined"
    >
        <!-- Styled as DatePicker's field. Callers' test ids land on the outer element. -->
        <TimeFieldRoot
            :id="props.id"
            v-slot="{ segments }"
            :key="fieldKey"
            :model-value="savedTime"
            :hour-cycle="12"
            granularity="minute"
            locale="en-US"
            :disabled="props.disabled"
            :aria-label="props.label"
            :aria-invalid="props.invalid ? 'true' : undefined"
            class="flex items-center tabular-nums"
            @keydown="onFieldKeydown"
            @focusout="onFieldFocusout"
        >
            <template v-for="(item, index) in segments" :key="`${item.part}-${index}`">
                <TimeFieldInput
                    v-if="item.part === 'literal'"
                    :part="item.part"
                    class="text-muted-foreground px-px"
                >
                    {{ item.value }}
                </TimeFieldInput>
                <!-- reka never shows AM/PM as empty, so until it is chosen this
                     field shows it as a placeholder, like an empty hour. -->
                <TimeFieldInput
                    v-else-if="item.part === 'dayPeriod'"
                    :part="item.part"
                    :data-placeholder="periodChosen ? undefined : ''"
                    class="focus:bg-accent focus:text-accent-foreground data-[placeholder]:text-muted-foreground rounded-sm px-0.5 outline-none"
                >
                    {{ periodChosen ? item.value : '––' }}
                </TimeFieldInput>
                <TimeFieldInput
                    v-else
                    :part="item.part"
                    class="focus:bg-accent focus:text-accent-foreground data-[placeholder]:text-muted-foreground rounded-sm px-0.5 outline-none"
                >
                    {{ item.value }}
                </TimeFieldInput>
            </template>
        </TimeFieldRoot>
    </div>
</template>
